import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Search, Eye, UserPlus, Plus, Trash2, FileText, Image as ImageIcon, Ban, X, Download } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Table, Column } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { ConfirmDeleteModal } from '../../components/ui/ConfirmDeleteModal';
import { Pagination } from '../../components/ui/Pagination';
import { ImageUpload } from '../../components/ui/ImageUpload';
import { PropertySelect } from '../../components/ui/PropertySelect';
import { api } from '../../services/api';
import { usePermissions } from '../../hooks/usePermissions';
import { Tenant, Property } from '../../types';
import { formatCurrencyInput, parseCurrencyToNumber } from '../../utils/validation';
import { formatDate, todayLocalDateString } from '../../utils/date';
import { buildCsv, downloadCsvFile } from '../../utils/exportCsv';
import { fetchAllPagesData } from '../../utils/fetchAllPages';

type TenantRow = Tenant & { propertyName?: string };

function mapApiTenantToTenant(item: any): TenantRow {
  const startDate = item.startDate
    ? (typeof item.startDate === 'string' ? item.startDate : new Date(item.startDate).toISOString().split('T')[0])
    : '';
  const endDate = item.endDate
    ? (typeof item.endDate === 'string' ? item.endDate : new Date(item.endDate).toISOString().split('T')[0])
    : '';
  return {
    id: item.id,
    name: item.name,
    email: item.email != null ? String(item.email) : '',
    phone: item.phone != null ? String(item.phone) : '',
    propertyId: item.propertyId,
    unitNumber: item.unitNumber,
    monthlyRent: Number(item.monthlyRent) || 0,
    startDate,
    endDate,
    status: item.status || 'active',
    contract: item.contract,
    idOficial: item.idOficial,
    propertyName: item.properties?.name ?? '',
    prospectId: item.prospectId ?? null,
    deliveryChecklistDone: item.deliveryChecklistDone ?? item.delivery_checklist_done ?? false,
    deliverySigned: item.deliverySigned ?? item.delivery_signed ?? false,
    deliveryPaid: item.deliveryPaid ?? item.delivery_paid ?? false,
  };
}

const TENANT_STATUS_CSV: Record<string, string> = {
  active: 'Activo',
  pending: 'Pendiente',
  expired: 'Expirado',
  terminado: 'Terminado',
  cancelado: 'Cancelado',
};

function filterTenantRows(
  list: TenantRow[],
  searchTerm: string,
  statusFilter: 'all' | 'active' | 'pending' | 'expired' | 'terminado' | 'cancelado',
  showUpcomingExpirations: boolean,
  expirationDays: number,
): TenantRow[] {
  return list.filter((tenant) => {
    const matchesSearch =
      tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || tenant.status === statusFilter;
    let matchesExpiration = true;
    if (showUpcomingExpirations) {
      const endDate = new Date(tenant.endDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysUntilExpiration = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      matchesExpiration = daysUntilExpiration >= 0 && daysUntilExpiration <= expirationDays;
    }
    return matchesSearch && matchesStatus && matchesExpiration;
  });
}

/** Fecha fin sugerida al elegir fecha inicio: un año después (YYYY-MM-DD, calendario local). */
function addOneYearToDateString(isoDate: string): string {
  const parts = isoDate.split('-').map(Number);
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return '';
  const next = new Date(y, m - 1, d);
  next.setFullYear(next.getFullYear() + 1);
  const yy = next.getFullYear();
  const mm = String(next.getMonth() + 1).padStart(2, '0');
  const dd = String(next.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const limit = 20;

export const Tenants: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'pending' | 'expired' | 'terminado' | 'cancelado'>('all');
  const [showUpcomingExpirations, setShowUpcomingExpirations] = useState(false);
  const [expirationDays, setExpirationDays] = useState(90);
  const [page, setPage] = useState(1);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [tenantPendingDelete, setTenantPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelTenant, setCancelTenant] = useState<{ id: string; name: string } | null>(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelForm, setCancelForm] = useState({
    confirm: false,
    cancellationDate: new Date().toISOString().split('T')[0],
    reason: '',
    penaltyWaivedInternal: false,
    penaltyAmountDisplay: '',
    assignedUserId: '',
  });

  const { data: cancelMaintenanceUsers = [] } = useQuery({
    queryKey: ['maintenance-users'],
    queryFn: () => api.getMaintenanceUsers(),
    enabled: isCancelModalOpen,
  });

  const [tenantForm, setTenantForm] = useState(() => {
    const today = todayLocalDateString();
    return {
      name: '',
      email: '',
      phone: '',
      propertyId: '',
      unitNumber: '',
      monthlyRentDisplay: '',
      monthlyRent: 0,
      startDate: today,
      endDate: addOneYearToDateString(today),
      status: 'active' as const,
      contract: '',
      idOficial: '',
    };
  });
  const [pendingDocuments, setPendingDocuments] = useState<{ title: string; fileUrl: string; fileType?: string }[]>([]);
  const [newDocument, setNewDocument] = useState({ title: '', fileUrl: '', fileType: '' });
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadProgress, setDocumentUploadProgress] = useState(0);
  const [exportingCsv, setExportingCsv] = useState(false);
  const documentProgressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { data: tenantsRes, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['tenants', page, searchTerm, statusFilter],
    queryFn: () =>
      api.getTenants({
        page,
        limit,
        search: searchTerm.trim() || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      }),
    staleTime: 60_000,
  });
  const apiTenantsResponseInvalid = useMemo(() => {
    const raw: any = tenantsRes;
    if (!raw) return false;

    // Caso 1: el backend devuelve directamente un array
    if (Array.isArray(raw)) return false;

    // Caso 2: el backend devuelve el shape paginado: { data: [...], total, page, limit, totalPages }
    if (
      typeof raw === 'object' &&
      Array.isArray(raw.data) &&
      typeof raw.total === 'number' &&
      typeof raw.page === 'number'
    ) {
      return false;
    }

    // Caso sospechoso: objeto tipo perfil del usuario (auth/me)
    const looksLikeAuthMe =
      typeof raw === 'object' &&
      raw.twoFactorEnabled !== undefined &&
      raw.role &&
      typeof raw.role === 'object' &&
      typeof raw.email === 'string';

    return looksLikeAuthMe;
  }, [tenantsRes]);

  const tenants: TenantRow[] = useMemo(() => {
    const raw = tenantsRes;
    const list: any[] = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
    return list.map(mapApiTenantToTenant);
  }, [tenantsRes]);
  const total = (tenantsRes && typeof tenantsRes === 'object' && !Array.isArray(tenantsRes) ? (tenantsRes as any).total : 0) ?? 0;
  const totalPages = (tenantsRes && typeof tenantsRes === 'object' && !Array.isArray(tenantsRes) ? (tenantsRes as any).totalPages : 0) ?? 0;
  const error = queryError ? (queryError as Error)?.message ?? 'Error al cargar los inquilinos' : null;

  const { data: propertiesRes } = useQuery({
    queryKey: ['properties', 'list'],
    queryFn: () => api.getProperties({ limit: 500 }),
    staleTime: 60_000,
    enabled: isAddModalOpen,
  });
  const properties: Property[] = useMemo(() => {
    const list = propertiesRes?.data ?? [];
    return Array.isArray(list) ? list.map((p: any) => ({
      id: p.id,
      name: p.name,
      address: p.address || '',
      city: p.city || '',
      state: p.state,
      unitCount: p.unitCount || 0,
      occupiedUnits: p.occupiedUnits ?? p.tenants?.length ?? 0,
      m2Total: p.m2Total || 0,
      pricePerM2: p.pricePerM2 || 0,
      totalValue: Number(p.totalValue) || 0,
      status: p.status || 'en-proyecto',
      coverImage: p.coverImage ?? p.cover_image,
      documents: p.documents || p.property_documents || [],
      createdAt: p.createdAt || new Date().toISOString(),
    })) : [];
  }, [propertiesRes]);

  const { data: unitsData } = useQuery({
    queryKey: ['units', tenantForm.propertyId],
    queryFn: () => api.getUnits(tenantForm.propertyId!),
    staleTime: 60_000,
    enabled: !!tenantForm.propertyId,
  });
  const unitsForProperty: any[] = useMemo(() => {
    if (Array.isArray(unitsData)) return unitsData;
    if (unitsData && Array.isArray((unitsData as any).data)) return (unitsData as any).data;
    return [];
  }, [unitsData]);

  type CreateTenantPayload = {
    tenantData: Record<string, unknown>;
    documents: Array<{ title: string; fileUrl: string; fileType?: string }>;
  };
  const createTenantMutation = useMutation({
    mutationFn: async (payload: CreateTenantPayload) => {
      const created = await api.createTenant(payload.tenantData as any);
      for (const doc of payload.documents) {
        await api.addTenantDocument(created.id, { title: doc.title, fileUrl: doc.fileUrl, fileType: doc.fileType });
      }
      return created;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      setIsAddModalOpen(false);
      setPage(1);
    },
  });

  const deleteTenantMutation = useMutation({
    mutationFn: (id: string) => api.deleteTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    },
  });
  const deleteError = deleteTenantMutation.isError ? (deleteTenantMutation.error as Error)?.message : null;

  const openCancelModal = (row: TenantRow) => {
    setCancelError(null);
    setCancelTenant({ id: row.id, name: row.name });
    setCancelForm({
      confirm: false,
      cancellationDate: new Date().toISOString().split('T')[0],
      reason: '',
      penaltyWaivedInternal: false,
      penaltyAmountDisplay: '',
      assignedUserId: '',
    });
    setIsCancelModalOpen(true);
  };

  const handleCancelContractSubmit = async () => {
    if (!cancelTenant) return;
    if (!cancelForm.confirm) {
      setCancelError('Confirma que estás seguro antes de cancelar el contrato.');
      return;
    }
    const reason = cancelForm.reason.trim();
    if (!reason) {
      setCancelError('La razón de cancelación es obligatoria.');
      return;
    }
    if (!cancelForm.cancellationDate) {
      setCancelError('La fecha de cancelación es obligatoria.');
      return;
    }
    if (!cancelForm.penaltyWaivedInternal) {
      if (!cancelForm.penaltyAmountDisplay.trim()) {
        setCancelError('Indica el monto de penalidad cobrada o marca sin penalidad por razones internas.');
        return;
      }
      const amt = parseCurrencyToNumber(formatCurrencyInput(cancelForm.penaltyAmountDisplay));
      if (!Number.isFinite(amt) || amt < 0) {
        setCancelError('Indica un monto de penalidad válido (o marca sin penalidad por razones internas).');
        return;
      }
    }

    try {
      setCancelSubmitting(true);
      setCancelError(null);
      await api.cancelTenantContract(cancelTenant.id, {
        cancellationDate: cancelForm.cancellationDate,
        reason,
        penaltyWaivedInternal: cancelForm.penaltyWaivedInternal,
        ...(!cancelForm.penaltyWaivedInternal
          ? { penaltyAmount: parseCurrencyToNumber(formatCurrencyInput(cancelForm.penaltyAmountDisplay)) }
          : {}),
        ...(cancelForm.assignedUserId.trim() ? { assignedUserId: cancelForm.assignedUserId.trim() } : {}),
      });
      setIsCancelModalOpen(false);
      setCancelTenant(null);
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
    } catch (err: any) {
      setCancelError(err?.message || 'Error al cancelar el contrato');
    } finally {
      setCancelSubmitting(false);
    }
  };

  const openAddModal = useCallback(() => {
    setModalError(null);
    const today = todayLocalDateString();
    setTenantForm({
      name: '',
      email: '',
      phone: '',
      propertyId: '',
      unitNumber: '',
      monthlyRentDisplay: '',
      monthlyRent: 0,
      startDate: today,
      endDate: addOneYearToDateString(today),
      status: 'active',
      contract: '',
      idOficial: '',
    });
    setPendingDocuments([]);
    setNewDocument({ title: '', fileUrl: '', fileType: '' });
    setIsAddModalOpen(true);
  }, []);

  const handleAddDocumentToPending = () => {
    if (!newDocument.title.trim() || !newDocument.fileUrl) return;
    setPendingDocuments((prev) => [...prev, { title: newDocument.title.trim(), fileUrl: newDocument.fileUrl, fileType: newDocument.fileType || 'pdf' }]);
    setNewDocument({ title: '', fileUrl: '', fileType: '' });
  };

  const handleRemovePendingDocument = (index: number) => {
    setPendingDocuments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDocumentFileChange = (fileUrl: string, fileType?: string) => {
    setNewDocument((prev) => ({ ...prev, fileUrl, fileType: fileType || 'pdf' }));
  };

  const handleDocumentFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocumentUploading(true);
    setDocumentUploadProgress(0);
    if (documentProgressIntervalRef.current) clearInterval(documentProgressIntervalRef.current);
    documentProgressIntervalRef.current = setInterval(() => {
      setDocumentUploadProgress((p) => {
        if (p >= 90) {
          if (documentProgressIntervalRef.current) {
            clearInterval(documentProgressIntervalRef.current);
            documentProgressIntervalRef.current = null;
          }
          return 90;
        }
        return p + 4;
      });
    }, 120);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
      setDocumentUploadProgress(100);
      const fileType = file.type === 'application/pdf' ? 'pdf' : file.type.split('/')[1] || 'pdf';
      handleDocumentFileChange(reader.result as string, fileType);
      setTimeout(() => {
        setDocumentUploading(false);
        setDocumentUploadProgress(0);
      }, 400);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  useEffect(() => {
    return () => {
      if (documentProgressIntervalRef.current) {
        clearInterval(documentProgressIntervalRef.current);
        documentProgressIntervalRef.current = null;
      }
    };
  }, []);

  const handleCreateTenant = (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    const { name, email, phone, propertyId, unitNumber, monthlyRent, startDate, endDate, status, contract, idOficial } = tenantForm;
    if (!name?.trim() || !email?.trim() || !phone?.trim() || !propertyId || !unitNumber?.trim() || !startDate || !endDate) {
      setModalError('Completa los campos obligatorios: nombre, email, teléfono, propiedad, unidad, fechas.');
      return;
    }
    if (monthlyRent <= 0) {
      setModalError('La renta mensual debe ser mayor a 0.');
      return;
    }
    createTenantMutation.mutate(
      {
        tenantData: {
          name: name.trim(),
          email: email.trim(),
          phone: phone.trim(),
          propertyId,
          unitNumber: unitNumber.trim(),
          monthlyRent,
          startDate,
          endDate,
          status,
          contract: contract || undefined,
          idOficial: idOficial || undefined,
        },
        documents: pendingDocuments,
      },
      {
        onError: (err: Error) => setModalError(err.message || 'Error al crear el inquilino'),
      },
    );
  };

  const filteredTenants = useMemo(
    () => filterTenantRows(tenants, searchTerm, statusFilter, showUpcomingExpirations, expirationDays),
    [searchTerm, statusFilter, showUpcomingExpirations, expirationDays, tenants],
  );

  const handleExportCsv = useCallback(async () => {
    setExportingCsv(true);
    try {
      const raw = await fetchAllPagesData((args) => api.getTenants(args));
      const all = raw.map(mapApiTenantToTenant);
      const rows = filterTenantRows(all, searchTerm, statusFilter, showUpcomingExpirations, expirationDays);
      const headers = [
        'Nombre',
        'Email',
        'Teléfono',
        'Propiedad',
        'Unidad',
        'Renta mensual',
        'Inicio contrato',
        'Fin contrato',
        'Estado',
        'Entrega',
      ];
      const csvRows = rows.map((row) => {
        const deliveryDone =
          row.deliveryChecklistDone === true && row.deliverySigned === true && row.deliveryPaid === true;
        return [
          row.name,
          row.email != null ? String(row.email) : '',
          row.phone != null ? String(row.phone) : '',
          row.propertyName ?? '',
          row.unitNumber ?? '',
          String(row.monthlyRent),
          row.startDate,
          row.endDate,
          TENANT_STATUS_CSV[row.status] ?? row.status,
          deliveryDone ? 'Completa' : 'Pendiente',
        ];
      });
      downloadCsvFile(`inquilinos-${todayLocalDateString()}.csv`, buildCsv(headers, csvRows));
    } catch (e) {
      alert((e as Error)?.message || 'Error al exportar CSV');
    } finally {
      setExportingCsv(false);
    }
  }, [searchTerm, statusFilter, showUpcomingExpirations, expirationDays]);

  const { hasPermission } = usePermissions();
  const canUpdateRentals = hasPermission('update_rentals') || hasPermission('manage_rentals');
  const canDeleteRentals = hasPermission('delete_rentals');

  const columns: Column<TenantRow>[] = [
    {
      header: 'Inquilino',
      className: '!whitespace-normal align-top min-w-[10rem] max-w-[14rem]',
      accessor: (row) => (
        <div className="flex flex-col gap-0.5 py-0.5">
          <span className="font-medium text-gray-900 leading-tight">{row.name}</span>
          <span className="text-xs text-gray-600 break-all leading-tight">{row.email}</span>
        </div>
      ),
    },
    {
      header: 'Propiedad',
      className: '!whitespace-normal align-top min-w-[9rem] max-w-[16rem]',
      accessor: (row) => {
        const prop = row.propertyName?.trim() || '—';
        const unit = row.unitNumber?.trim() || '—';
        return (
          <span className="text-sm text-gray-900 leading-snug">
            {prop} – Unidad {unit}
          </span>
        );
      },
    },
    {
      header: 'Contrato',
      className: '!whitespace-normal align-top',
      accessor: (row) => (
        <div className="flex flex-col gap-0.5 text-xs text-gray-900 tabular-nums whitespace-nowrap">
          <span>{formatDate(row.startDate)}</span>
          <span>{formatDate(row.endDate)}</span>
        </div>
      ),
    },
    {
      header: 'Estado',
      className: 'whitespace-nowrap',
      accessor: (row) => {
        const statusConfig = {
          active: { label: 'Activo', className: 'bg-green-100 text-green-800' },
          pending: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' },
          expired: { label: 'Expirado', className: 'bg-red-100 text-red-800' },
          terminado: { label: 'Terminado', className: 'bg-blue-100 text-blue-800' },
          cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800' },
        };
        const config = statusConfig[row.status] || { label: row.status, className: 'bg-gray-100 text-gray-800' };
        return (
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.className}`}>
            {config.label}
          </span>
        );
      },
    },
    {
      header: 'Entrega',
      className: '!whitespace-normal align-top',
      accessor: (row) => {
        const checklist = row.deliveryChecklistDone === true;
        const signed = row.deliverySigned === true;
        const paid = row.deliveryPaid === true;
        const done = [checklist, signed, paid].filter(Boolean).length;
        const complete = done === 3;
        const tip = `Checklist: ${checklist ? 'sí' : 'no'} · Firma: ${signed ? 'sí' : 'no'} · Pago: ${paid ? 'sí' : 'no'}`;
        return (
          <div
            className="flex flex-col gap-1"
            title={tip}
          >
            <span
              className={`inline-flex w-fit px-2 py-1 rounded-full text-xs font-semibold ${
                complete ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'
              }`}
            >
              {complete ? 'Completa' : 'Pendiente'}
            </span>
            <span className="text-xs text-gray-500">{done}/3</span>
          </div>
        );
      },
    },
    {
      header: 'Acciones',
      className: 'whitespace-nowrap w-px',
      accessor: (row) => (
        <div className="flex flex-nowrap items-center justify-end gap-0.5">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/rentas/inquilinos/${row.id}`);
            }}
            className="inline-flex items-center justify-center p-2 rounded-lg text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
            title="Ver inquilino"
            aria-label="Ver inquilino"
          >
            <Eye className="w-4 h-4 shrink-0" />
          </button>
          {canUpdateRentals && row.status !== 'cancelado' && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                openCancelModal(row);
              }}
              className="inline-flex items-center justify-center p-2 rounded-lg text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              title="Cancelar contrato"
              aria-label="Cancelar contrato"
            >
              <X className="w-4 h-4 shrink-0" strokeWidth={2.25} />
            </button>
          )}
          {canDeleteRentals && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setTenantPendingDelete({ id: row.id, name: row.name });
              }}
              disabled={
                deleteTenantMutation.isPending && tenantPendingDelete?.id === row.id
              }
              className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title="Eliminar inquilino"
              aria-label="Eliminar inquilino"
            >
              <Trash2 className="w-4 h-4 shrink-0" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 min-w-0 max-w-full">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center space-x-3">
          <Users className="w-6 h-6 text-primary" />
          <h1 className="text-2xl font-bold text-gray-900">Inquilinos</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            icon={Download}
            onClick={handleExportCsv}
            disabled={exportingCsv}
            className="flex items-center gap-2"
          >
            {exportingCsv ? 'Exportando…' : 'Exportar CSV'}
          </Button>
          <Button onClick={openAddModal} variant="primary" className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            Agregar inquilino
          </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-4 min-w-0 max-w-full">
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Buscar por nombre o email..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setPage(1); }}
                className="w-full pl-10 pr-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1); }}
              className="px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="all">Todos los estados</option>
              <option value="active">Activo</option>
              <option value="pending">Pendiente</option>
              <option value="expired">Expirado</option>
              <option value="terminado">Terminado</option>
              <option value="cancelado">Cancelado</option>
            </select>
          </div>
          <div className="flex items-center gap-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUpcomingExpirations}
                onChange={(e) => setShowUpcomingExpirations(e.target.checked)}
                className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
              />
              <span className="text-sm font-medium text-gray-700">
                Mostrar próximos vencimientos
              </span>
            </label>
            {showUpcomingExpirations && (
              <select
                value={expirationDays}
                onChange={(e) => setExpirationDays(Number(e.target.value))}
                className="px-3 py-1.5 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                onClick={(e) => e.stopPropagation()}
              >
                <option value="30">En los próximos 30 días</option>
                <option value="60">En los próximos 60 días</option>
                <option value="90">En los próximos 90 días</option>
                <option value="180">En los próximos 180 días</option>
              </select>
            )}
          </div>
        </div>

        {loading && (
          <div className="flex justify-center py-8 text-gray-500">Cargando inquilinos...</div>
        )}
        {(error || deleteError) && (
          <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3">
            {error || deleteError}
          </div>
        )}
        {!loading && !error && filteredTenants.length > 0 && (
          <>
            <Table
              data={filteredTenants}
              columns={columns}
              className="w-full rounded-lg border border-gray-100 [&_table]:min-w-[760px] [&_th]:px-3 [&_th]:py-2.5 [&_td]:px-3 [&_td]:py-2.5"
            />
            {totalPages > 1 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            )}
          </>
        )}
        {!loading && !error && apiTenantsResponseInvalid && (
          <div className="text-center py-12 px-4">
            <Users className="w-12 h-12 text-red-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-red-700 mb-2">Respuesta inesperada del servidor</h3>
            <p className="text-sm text-red-600 max-w-md mx-auto mb-6">
              La API de <code>/tenants</code> devolvió un objeto tipo <code>/auth/me</code> en vez del listado de inquilinos.
              Esto suele indicar un problema de proxy/ruteo o que el backend no está respondiendo como se espera para este rol.
            </p>
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 max-w-lg mx-auto">
              {tenantsRes && typeof tenantsRes === 'object'
                ? `Recibido: ${(tenantsRes as any).name || '(sin nombre)'} (${(tenantsRes as any).email || 'sin email'})`
                : 'Recibido: (vacío)'}
            </div>
          </div>
        )}
        {!loading && !error && !apiTenantsResponseInvalid && filteredTenants.length === 0 && (
          <div className="text-center py-12 px-4">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-1">
              {tenants.length === 0 ? 'No hay inquilinos registrados' : 'No hay resultados'}
            </h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
              {tenants.length === 0
                ? 'Los inquilinos se crean al aprobar un prospecto. Ve a Prospectos, abre un prospecto con solicitud y usa "Agregar a Inquilinos" (o aprueba uno nuevo).'
                : 'Prueba con otro filtro o término de búsqueda.'}
            </p>
            {tenants.length === 0 && (
              <Link
                to="/rentas/prospectos"
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 text-sm font-medium"
              >
                <UserPlus className="w-4 h-4" />
                Ir a Prospectos
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Modal Agregar inquilino */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Agregar inquilino"
        size="xl"
      >
        <form onSubmit={handleCreateTenant} className="space-y-6">
          {modalError && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {modalError}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nombre <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={tenantForm.name}
                onChange={(e) => setTenantForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="Nombre completo"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input
                type="email"
                value={tenantForm.email}
                onChange={(e) => setTenantForm((prev) => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={tenantForm.phone}
                onChange={(e) => setTenantForm((prev) => ({ ...prev, phone: e.target.value }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="10 dígitos"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Propiedad <span className="text-red-500">*</span></label>
              <PropertySelect
                properties={properties}
                value={tenantForm.propertyId}
                onChange={(propertyId) => setTenantForm((prev) => ({ ...prev, propertyId, unitNumber: '' }))}
                placeholder="Seleccione una propiedad"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Unidad <span className="text-red-500">*</span></label>
              {unitsForProperty.length > 0 ? (
                <select
                  value={tenantForm.unitNumber}
                  onChange={(e) => setTenantForm((prev) => ({ ...prev, unitNumber: e.target.value }))}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Seleccionar unidad</option>
                  {unitsForProperty.map((u) => (
                    <option key={u.id} value={u.unitNumber}>{u.unitNumber}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={tenantForm.unitNumber}
                  onChange={(e) => setTenantForm((prev) => ({ ...prev, unitNumber: e.target.value }))}
                  className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: 101"
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Renta mensual <span className="text-red-500">*</span></label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={tenantForm.monthlyRentDisplay}
                  onChange={(e) => {
                    const value = formatCurrencyInput(e.target.value);
                    setTenantForm((prev) => ({ ...prev, monthlyRentDisplay: value, monthlyRent: parseCurrencyToNumber(value) }));
                  }}
                  onBlur={() => {
                    if (tenantForm.monthlyRent > 0) {
                      setTenantForm((prev) => ({ ...prev, monthlyRentDisplay: prev.monthlyRent.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 }) }));
                    }
                  }}
                  onFocus={() => {
                    if (tenantForm.monthlyRent > 0) {
                      setTenantForm((prev) => ({ ...prev, monthlyRentDisplay: String(prev.monthlyRent) }));
                    }
                  }}
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ej: 15000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio <span className="text-red-500">*</span></label>
              <input
                type="date"
                min={todayLocalDateString()}
                value={tenantForm.startDate}
                onChange={(e) => {
                  const startDate = e.target.value;
                  setTenantForm((prev) => ({
                    ...prev,
                    startDate,
                    endDate: startDate ? addOneYearToDateString(startDate) : '',
                  }));
                }}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin <span className="text-red-500">*</span></label>
              <input
                type="date"
                min={tenantForm.startDate || todayLocalDateString()}
                value={tenantForm.endDate}
                onChange={(e) => setTenantForm((prev) => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
              <select
                value={tenantForm.status}
                onChange={(e) => setTenantForm((prev) => ({ ...prev, status: e.target.value as any }))}
                className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="active">Activo</option>
                <option value="pending">Pendiente</option>
                <option value="expired">Expirado</option>
                <option value="terminado">Terminado</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contrato</label>
              <ImageUpload
                currentImage={tenantForm.contract}
                onImageChange={(url) => setTenantForm((prev) => ({ ...prev, contract: url }))}
                label="Subir contrato"
                acceptPdf
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Identificación oficial</label>
              <ImageUpload
                currentImage={tenantForm.idOficial}
                onImageChange={(url) => setTenantForm((prev) => ({ ...prev, idOficial: url }))}
                label="Subir identificación"
                acceptPdf
              />
            </div>
          </div>

          {/* Documentos adicionales del expediente (no son los de la solicitud pública) */}
          <div className="border-t border-gray-200 pt-6">
            <h4 className="text-sm font-semibold text-gray-800 mb-1">Documentos adicionales</h4>
            <p className="text-xs text-gray-500 mb-3">
              Se guardan en el expediente del inquilino. Los archivos que subió el prospecto en el formulario de la solicitud se consultan después en la ficha del inquilino, pestaña Documentos.
            </p>
            {pendingDocuments.length > 0 && (
              <div className="space-y-2 mb-4">
                {pendingDocuments.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-800">{doc.title}</span>
                    <button
                      type="button"
                      onClick={() => handleRemovePendingDocument(index)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title="Quitar documento"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Agregar Nuevo Documento</h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Título del Documento</label>
                  <input
                    type="text"
                    value={newDocument.title}
                    onChange={(e) => setNewDocument((prev) => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary placeholder-gray-400"
                    placeholder="Ej: Título de Propiedad"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Archivo</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={documentUploading}
                    onChange={handleDocumentFileSelect}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent/90 disabled:opacity-50"
                  />
                  {documentUploading && (
                    <div className="mt-2 h-1.5 w-full bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-[width] duration-200 ease-out"
                        style={{ width: `${documentUploadProgress}%` }}
                      />
                    </div>
                  )}
                  {newDocument.fileUrl && !documentUploading && (
                    <div className="mt-2 flex items-center gap-2 p-2 rounded-lg bg-green-50 border border-green-200">
                      {newDocument.fileType?.toLowerCase() === 'pdf' ? (
                        <FileText className="w-8 h-8 text-red-600 shrink-0" />
                      ) : (
                        <ImageIcon className="w-8 h-8 text-blue-600 shrink-0" />
                      )}
                      <span className="text-sm text-green-700">Archivo listo. Escribe el título y pulsa &quot;Agregar Documento&quot;.</span>
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAddDocumentToPending}
                    disabled={!newDocument.title.trim() || !newDocument.fileUrl}
                    className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:cursor-not-allowed enabled:bg-green-600 enabled:text-white enabled:hover:bg-green-700 disabled:bg-gray-400 disabled:text-gray-200"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Agregar Documento</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsAddModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createTenantMutation.isPending}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {createTenantMutation.isPending && (
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              <span>{createTenantMutation.isPending ? 'Creando...' : 'Crear inquilino'}</span>
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isCancelModalOpen}
        onClose={() => !cancelSubmitting && setIsCancelModalOpen(false)}
        title="Cancelar contrato"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-900">
              ¿Estás seguro de cancelar este contrato{cancelTenant?.name ? ` de "${cancelTenant.name}"` : ''}?
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Esta acción marcará al inquilino como <strong>cancelado</strong> y liberará la unidad. No se puede deshacer desde aquí.
            </p>
          </div>

          {cancelError && (
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm">
              {cancelError}
            </div>
          )}

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cancelForm.confirm}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, confirm: e.target.checked }))}
              disabled={cancelSubmitting}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-800">Sí, estoy seguro de cancelar el contrato</span>
          </label>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Fecha de cancelación <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={cancelForm.cancellationDate}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, cancellationDate: e.target.value }))}
              disabled={cancelSubmitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Razón <span className="text-red-500">*</span>
            </label>
            <textarea
              value={cancelForm.reason}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, reason: e.target.value }))}
              disabled={cancelSubmitting}
              rows={3}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
              placeholder="Describe el motivo de la cancelación"
            />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={cancelForm.penaltyWaivedInternal}
              onChange={(e) =>
                setCancelForm((prev) => ({
                  ...prev,
                  penaltyWaivedInternal: e.target.checked,
                  penaltyAmountDisplay: e.target.checked ? '' : prev.penaltyAmountDisplay,
                }))
              }
              disabled={cancelSubmitting}
              className="mt-1 rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-800">Sin penalidad por razones internas</span>
          </label>

          {!cancelForm.penaltyWaivedInternal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Monto de penalidad cobrada <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">$</span>
                <input
                  type="text"
                  value={cancelForm.penaltyAmountDisplay}
                  onChange={(e) =>
                    setCancelForm((prev) => ({ ...prev, penaltyAmountDisplay: formatCurrencyInput(e.target.value) }))
                  }
                  disabled={cancelSubmitting}
                  className="w-full pl-8 pr-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
                  placeholder="Ej: 5000"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Inspección de salida (opcional)</label>
            <select
              value={cancelForm.assignedUserId}
              onChange={(e) => setCancelForm((prev) => ({ ...prev, assignedUserId: e.target.value }))}
              disabled={cancelSubmitting}
              className="w-full px-3 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 text-sm"
            >
              <option value="">Sin inspección de salida</option>
              {cancelMaintenanceUsers.map((u: { id: string; name: string }) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={() => setIsCancelModalOpen(false)}
              disabled={cancelSubmitting}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cerrar
            </button>
            <button
              type="button"
              onClick={handleCancelContractSubmit}
              disabled={cancelSubmitting || !cancelForm.confirm}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              <Ban className="w-4 h-4" />
              {cancelSubmitting ? 'Cancelando...' : 'Confirmar cancelación'}
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmDeleteModal
        isOpen={tenantPendingDelete !== null}
        onClose={() => {
          if (!deleteTenantMutation.isPending) setTenantPendingDelete(null);
        }}
        onConfirm={() => {
          if (!tenantPendingDelete) return;
          deleteTenantMutation.mutate(tenantPendingDelete.id, {
            onSuccess: () => setTenantPendingDelete(null),
            onError: () => setTenantPendingDelete(null),
          });
        }}
        message={
          tenantPendingDelete ? (
            <>
              ¿Seguro que deseas eliminar al inquilino <strong>{tenantPendingDelete.name}</strong>?
            </>
          ) : null
        }
        isLoading={deleteTenantMutation.isPending}
      />
    </div>
  );
};

